import React, {useEffect, useRef} from 'react'
import ApexCharts, {ApexOptions} from 'apexcharts'
import {getCSS, getCSSVariableValue} from '../../../assets/ts/_utils'
import {useThemeMode} from '../../layout/theme-mode/ThemeModeProvider'
import {SprintReportStatistics} from '../../../../app/modules/reports/components/sprint-user/core/_models'

type Props = {
  className: string
  statistics: SprintReportStatistics[]
}

const ChartSprintsPerformance: React.FC<Props> = ({className, statistics}) => {
  const chartRef = useRef<HTMLDivElement | null>(null)
  const categories = statistics.map((sprint) => `${sprint.sprint_name} #${sprint.id}`).reverse()
  const delivereds = statistics.map((sprint) => sprint.tasks_delivered).reverse()
  const pendents = statistics.map((sprint) => sprint.tasks_backlog + sprint.tasks_in_progress).reverse()
  const {mode} = useThemeMode()
  const refreshChart = () => {
    if (!chartRef.current) return
    const height = parseInt(getCSS(chartRef.current, 'height'))
    const chart = new ApexCharts(
      chartRef.current,
      getChartOptions(height, categories, delivereds, pendents)
    )
    if (chart) chart.render()

    return chart
  }

  useEffect(() => {
    const chart = refreshChart()

    return () => {
      if (chart) chart.destroy()
    }
  }, [chartRef, mode])

  return (
    <div className={`card ${className}`}>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>Performance</span>

          <span className='text-muted fw-semibold fs-7'>
            Quantidade de tarefas entregues por sprint
          </span>
        </h3>
      </div>

      <div className='card-body'>
        <div
          ref={chartRef}
          id='kt_charts_widget_8_chart'
          style={{height: '150px'}}
          className='card-rounded-bottom'
        ></div>
      </div>
    </div>
  )
}

export {ChartSprintsPerformance}

function getChartOptions(
  height: number,
  categories: string[],
  delivereds: number[],
  pendents: number[]
): ApexOptions {
  const labelColor = getCSSVariableValue('--kt-gray-500')
  const borderColor = getCSSVariableValue('--kt-gray-200')
  const strokeColor = getCSSVariableValue('--kt-gray-300')

  const color1 = getCSSVariableValue('--kt-success')
  const color1Light = getCSSVariableValue('--kt-success-light')

  const color2 = getCSSVariableValue('--kt-primary')
  const color2Light = getCSSVariableValue('--kt-primary-light')

  // const color3 = getCSSVariableValue('--kt-primary')
  // const color3Light = getCSSVariableValue('--kt-primary-light')

  return {
    series: [
      {
        name: 'Entregues',
        data: delivereds,
      },
      {
        name: 'Não entregues',
        data: pendents,
      },
    ],
    chart: {
      fontFamily: 'inherit',
      type: 'area',
      height: height,
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
      sparkline: {
        enabled: true,
      },
    },
    plotOptions: {},
    legend: {
      show: false,
    },
    dataLabels: {
      enabled: false,
    },
    fill: {
      type: 'solid',
      opacity: 1,
    },
    stroke: {
      curve: 'smooth',
      show: true,
      width: 2,
      colors: [color1, color2],
    },
    xaxis: {
      offsetX: 0,
      offsetY: 0,
      categories: categories,
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        show: true,
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
      },
      crosshairs: {
        show: false,
        position: 'front',
        stroke: {
          color: strokeColor,
          width: 1,
          dashArray: 3,
        },
      },
      tooltip: {
        enabled: true,
        formatter: undefined,
        offsetY: 0,
        style: {
          fontSize: '12px',
        },
      },
    },
    yaxis: {
      labels: {
        show: true,
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
      },
    },
    states: {
      normal: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      hover: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      active: {
        allowMultipleDataPointsSelection: false,
        filter: {
          type: 'none',
          value: 0,
        },
      },
    },
    tooltip: {
      style: {
        fontSize: '12px',
      },
      y: {
        formatter: function (val) {
          return val.toString()
        },
      },
    },
    colors: [color1Light, color2Light],
    grid: {
      borderColor: borderColor,
      strokeDashArray: 4,
      padding: {
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
      },
    },
    markers: {
      colors: [color1, color2],
      strokeColors: [color1, color2],
      strokeWidth: 3,
    },
  }
}
